import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@hooks/useStores';
import $api from '@http';
import { useToastContext } from '@contexts/ToastContext';
import ModeratorService from '@service/ModeratorService';
import { Modal } from '@components/common/ui/Modal';
import { Loader } from '@components/common/ui';
import { ArrowLeftIcon, FileTextIcon, GlobeIcon, GridIcon, ListIcon } from '@components/common/ui/icons';
import { CustomSelect } from '@components/common/ui/CustomSelect';
import { NoteCard } from '@components/notes/NoteCard';
import * as styles from '@pages/PublicProfilePage.module.css';

type SortOption = 'recent' | 'popular' | 'oldest';

interface PublicUser {
    id: string;
    name: string;
    login: string;
    avatarUrl?: string | null;
    about?: string | null;
}

interface PublicNote {
    id: string;
    title: string;
    excerpt?: string;
    rendered?: string;
    searchableContent?: string;
    meta?: { excerpt?: string; views?: number };
    updatedAt: string;
    createdAt: string;
    isFavorite?: boolean;
    isShared?: boolean;
}

const getInitials = (name: string) =>
    name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'U';

const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'short' });

export const PublicProfilePage: React.FC = () => {
    const { t } = useTranslation();
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const toast = useToastContext();
    const authStore = useAuthStore();

    const [user, setUser] = useState<PublicUser | null>(null);
    const [notes, setNotes] = useState<PublicNote[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<SortOption>('recent');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [blockModalOpen, setBlockModalOpen] = useState(false);
    const [noteToBlock, setNoteToBlock] = useState<PublicNote | null>(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);

    useEffect(() => {
        if (!userId) return;
        setLoading(true);

        Promise.all([
            $api.get(`/users/${userId}`).catch(() => ({ data: null })),
            $api.get('/notes/public').catch(() => ({ data: [] })),
        ])
            .then(([userRes, notesRes]) => {
                if (!userRes.data) { setError(t('publicProfile.userNotFound')); return; }

                const raw = userRes.data;
                const mappedUser: PublicUser = {
                    id: raw.id || raw._id,
                    name: raw.name || raw.login || t('userMenu.userFallback'),
                    login: raw.login,
                    avatarUrl: raw.avatarUrl || null,
                    about: raw.about || null,
                };

                const publicNotes: PublicNote[] = (Array.isArray(notesRes.data) ? notesRes.data : [])
                    .filter((n: any) => n.ownerId && String(n.ownerId) === String(mappedUser.id))
                    .map((n: any) => {
                        const excerpt =
                            n.meta?.excerpt || n.meta?.searchableContent?.trim().slice(0, 200);
                        return {
                            id: n.id,
                            title: n.title || t('notifications.untitled'),
                            excerpt,
                            rendered: n.rendered,
                            searchableContent: n.meta?.searchableContent,
                            meta: { excerpt, views: n.meta?.views ?? 0 },
                            updatedAt: n.updatedAt,
                            createdAt: n.createdAt,
                            isFavorite: n.meta?.isFavorite ?? false,
                            isShared: n.isPublic ?? false,
                        };
                    });

                setUser(mappedUser);
                setNotes(publicNotes);
                setError(null);

                const myId = authStore.user?.id;
                if (myId && myId !== mappedUser.id) {
                    $api.get(`/users/${mappedUser.id}/is-following`)
                        .then((r) => setIsFollowing(r.data.isFollowing))
                        .catch(() => {});
                    $api.get(`/users/${mappedUser.id}/followers`)
                        .then((r) => setFollowersCount(Array.isArray(r.data) ? r.data.length : 0))
                        .catch(() => {});
                }
            })
            .catch((e: any) => {
                setError(e?.response?.data?.message || t('publicProfile.loadFailed'));
                toast.error(t('publicProfile.loadUserFailed'));
            })
            .finally(() => setLoading(false));
    }, [userId, t]); // eslint-disable-line react-hooks/exhaustive-deps

    const sortedNotes = useMemo(() => {
        const copy = [...notes];
        copy.sort((a, b) => {
            if (sortBy === 'popular') return (b.meta?.views ?? 0) - (a.meta?.views ?? 0);
            if (sortBy === 'oldest')
                return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
            return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        });
        return copy;
    }, [notes, sortBy]);

    const isModerator = authStore.user?.role === 'moderator';

    const handleBlockNote = (noteId: string) => {
        const note = notes.find((n) => n.id === noteId);
        if (note) { setNoteToBlock(note); setBlockModalOpen(true); }
    };

    const handleConfirmBlock = async () => {
        if (!noteToBlock) return;
        try {
            await ModeratorService.blockNote(noteToBlock.id);
            setNotes((prev) => prev.filter((n) => n.id !== noteToBlock.id));
            toast.success(t('moderator.noteBlocked'));
            setBlockModalOpen(false);
            setNoteToBlock(null);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || t('moderator.failedToBlockNote'));
        }
    };

    if (loading) return <Loader fullScreen variant="spinner" size="lg" text={t('publicProfile.loading')} />;

    if (error || !user) {
        return (
            <div className={styles.page}>
                <header className={styles.topbar}>
                    <button className={styles.topbarBack} onClick={() => navigate(-1)}>
                        <ArrowLeftIcon className={styles.topbarBackIcon} />
                        {t('publicProfile.title')}
                    </button>
                </header>
                <div className={styles.content}>
                    <div className={styles.emptyState}>
                        <GlobeIcon className={styles.emptyIcon} />
                        <p className={styles.emptyTitle}>{t('publicProfile.notFoundTitle')}</p>
                        <p className={styles.emptyText}>
                            {t('publicProfile.notFoundText')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {/* Topbar */}
            <header className={styles.topbar}>
                <button className={styles.topbarBack} onClick={() => navigate(-1)}>
                    <ArrowLeftIcon className={styles.topbarBackIcon} />
                    {t('publicProfile.title')}
                </button>
            </header>

            <div className={styles.content}>
                {/* User header */}
                <div className={styles.userHeader}>
                    <div className={styles.avatar}>
                        {user.avatarUrl ? (
                            <img src={user.avatarUrl} alt={user.name} className={styles.avatarImage} />
                        ) : (
                            getInitials(user.name)
                        )}
                    </div>
                    <div className={styles.userInfo}>
                        <h1 className={styles.userName}>{user.name}</h1>
                        <p className={styles.userLogin}>@{user.login}</p>
                        <p className={styles.userMeta}>
                            {t('publicProfile.notesCount', { count: sortedNotes.length })}
                            {followersCount > 0 && ` · ${t('publicProfile.followersCount', { count: followersCount })}`}
                        </p>
                        {user.about && <p className={styles.userBio}>{user.about}</p>}
                    </div>
                    <div className={styles.userActions}>
                        {authStore.user && authStore.user.id !== user.id && (
                            <button
                                className={isFollowing ? styles.btnSecondary : styles.btnPrimary}
                                disabled={followLoading}
                                onClick={async () => {
                                    setFollowLoading(true);
                                    try {
                                        if (isFollowing) {
                                            await $api.delete(`/users/${user.id}/follow`);
                                            setIsFollowing(false);
                                            setFollowersCount((c) => Math.max(0, c - 1));
                                            toast.success(t('publicProfile.unfollowed', { login: user.login }));
                                        } else {
                                            await $api.post(`/users/${user.id}/follow`);
                                            setIsFollowing(true);
                                            setFollowersCount((c) => c + 1);
                                            toast.success(t('publicProfile.followed', { login: user.login }));
                                        }
                                    } catch {
                                        // error toast handled by axios
                                    } finally {
                                        setFollowLoading(false);
                                    }
                                }}
                            >
                                {isFollowing ? t('publicProfile.unfollow') : t('publicProfile.follow')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Notes section */}
                <div>
                    {/* Section header */}
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionLabel}>
                            <span className={styles.sectionLabelText}>
                                {t('publicProfile.userPublicNotes', { name: user.name })}
                            </span>
                            <div className={styles.sectionDivider} />
                        </div>
                        <div className={styles.controls}>
                            <CustomSelect
                                value={sortBy}
                                options={[
                                    { value: 'recent', label: t('publicProfile.sortRecent') },
                                    { value: 'popular', label: t('publicProfile.sortPopular') },
                                    { value: 'oldest', label: t('publicProfile.sortOldest') },
                                ]}
                                onChange={(v) => setSortBy(v as SortOption)}
                            />
                            <div className={styles.viewToggle}>
                                <button
                                    className={styles.viewBtn}
                                    style={{ color: viewMode === 'grid' ? 'var(--accent-color)' : undefined }}
                                    onClick={() => setViewMode('grid')}
                                    title={t('home.gridView')}
                                >
                                    <GridIcon className={styles.viewBtnIcon} />
                                </button>
                                <button
                                    className={styles.viewBtn}
                                    style={{ color: viewMode === 'list' ? 'var(--accent-color)' : undefined }}
                                    onClick={() => setViewMode('list')}
                                    title={t('home.listView')}
                                >
                                    <ListIcon className={styles.viewBtnIcon} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Notes */}
                    {sortedNotes.length === 0 ? (
                        <div className={styles.emptyState}>
                            <GlobeIcon className={styles.emptyIcon} />
                            <p className={styles.emptyTitle}>{t('publicProfile.noPublicNotes')}</p>
                            <p className={styles.emptyText}>
                                {t('publicProfile.noPublicNotesHint', { name: user.name })}
                            </p>
                        </div>
                    ) : (
                        <div className={viewMode === 'grid' ? styles.notesGrid : styles.notesList}>
                            {sortedNotes.map((note) => (
                                <NoteCard
                                    key={note.id}
                                    note={note}
                                    viewMode={viewMode}
                                    readOnly
                                    showBlockButton={isModerator}
                                    onBlock={handleBlockNote}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Modal
                isOpen={blockModalOpen}
                onClose={() => setBlockModalOpen(false)}
                title={t('moderator.blockPublicNoteTitle')}
                message={t('moderator.blockPublicNoteMessage', { title: noteToBlock?.title || t('notifications.untitled') })}
                confirmText={t('moderator.block')}
                cancelText={t('topbar.cancel')}
                onConfirm={handleConfirmBlock}
                variant="danger"
            />
        </div>
    );
};
